import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';

import { ErrorStatusCodeEnum } from '@core/enums/error-status-codes.enum';
import { AuthService } from '@core/services/auth.service';
import { ErrorService } from '@core/services/error.service';
import { TokenService } from '@core/services/token.service';
import { environment as env } from '@environments/environment';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly errorService = inject(ErrorService);
  private readonly tokenService = inject(TokenService);
  private readonly destroyRef = inject(DestroyRef);

  private isRefreshingToken = false;
  private tokenSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private destroyQueue$: Subject<boolean> = new Subject();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(req).pipe(
      catchError(response => {
        if (response.status === 401) {
          return this.handle401Error(req, next, response);
        }
        return throwError(() => response);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, response: any): any {
    const { errorCode } = response.error;
    switch (errorCode) {
      case ErrorStatusCodeEnum.TOKEN_EXPIRED:
        console.log('refresh-token', ErrorStatusCodeEnum.TOKEN_EXPIRED, response);
        return this.handleRefresh(request, next);
      case ErrorStatusCodeEnum.TOKEN_REFRESH_EXPIRED:
      case ErrorStatusCodeEnum.ERROR_AUTH:
        console.log('refresh-token', ErrorStatusCodeEnum.TOKEN_REFRESH_EXPIRED, response);
        this.errorService.hasDialog = false;
        this.endSession();
        break;
    }
    return throwError(() => response);
  }

  private handleRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    console.log('isRefreshingToken', this.isRefreshingToken);
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      if (req.url.includes(env.api.authenticate)) {
        return this.getTempToken(next, req);
      }

      if (req.url.includes(env.api.identities)) {
        this.endSession();
        this.isRefreshingToken = false;
        return of(null);
      }

      if (this.tokenService.refreshToken?.length) {
        return this.refresh(next, req);
      } else {
        console.log('Не смог заполучить рефреш токен');
        this.endSession();
        this.destroyQueue$.next(true);
        return next.handle(req);
      }
    } else {
      console.log('очередь');
      return this.tokenSubject$.pipe(
        filter(token => token != null),
        switchMap(() => next.handle(req)),
        takeUntilDestroyed(this.destroyRef)
      );
    }
  }

  private refresh(next: HttpHandler, req: HttpRequest<any>): Observable<any> {
    return this.authService.refresh().pipe(
      switchMap(res => {
        if (res.status) {
          this.tokenService.setTokens(res.meta);
          this.tokenSubject$.next(this.tokenService.accessToken);
        }
        return next.handle(req);
      }),
      finalize(() => (this.isRefreshingToken = false))
    );
  }

  private getTempToken(next: HttpHandler, req: HttpRequest<any>): Observable<any> {
    return this.authService.hello().pipe(
      switchMap(helloRes => {
        this.authService.temporaryToken = helloRes.meta.temporaryToken;
        return next.handle(req);
      }),
      finalize(() => (this.isRefreshingToken = false))
    );
  }

  private endSession(message?: string): void {
    if (!this.errorService.hasDialog) {
      this.errorService.hasDialog = true;
      this.authService.endSession$.next(message);
    }
  }
}
